import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Spinner } from '../Spinner/Spinner';
import styles from './Button.module.css';

type TrackingValue = string | number | boolean;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'link' | 'link-secondary' | 'link-tertiary' | 'tertiary';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  fullWidth?: boolean;
  trackEvent?: string;
  trackData?: Record<string, TrackingValue | null | undefined>;
  children: ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  disabled,
  fullWidth = false,
  trackEvent,
  trackData,
  className,
  children,
  ...props
}: ButtonProps) {
  const isDisabled = disabled || isLoading;

  const classNames = [
    styles.button,
    styles[variant],
    styles[size],
    isLoading ? styles.loading : '',
    fullWidth ? styles.fullWidth : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const trackingAttributes: Record<string, string> = {};
  if (trackEvent) {
    trackingAttributes['data-track'] = trackEvent;

    if (trackData) {
      for (const [key, value] of Object.entries(trackData)) {
        if (value == null) continue;
        const attrKey = `data-${key.replace(/[A-Z]/g, m => `-${m.toLowerCase()}`)}`;
        trackingAttributes[attrKey] = String(value);
      }
    }
  }

  return (
    <button
      className={classNames}
      disabled={isDisabled}
      aria-busy={isLoading}
      aria-disabled={isDisabled}
      {...trackingAttributes}
      {...props}
    >
      {isLoading && <Spinner size="sm" className={styles.spinner} />}
      {children}
    </button>
  );
}

Button.displayName = 'Button';
